import { z } from "zod";
import { validQuantityPrecision } from "./quantity.js";

const dateTime = z.string().datetime({ offset: true });
const preciseQuantity = z.number().refine(validQuantityPrecision,
  "Miktar en fazla 12 ondalık ve 15 anlamlı basamak içermelidir.");

export const materialScrapWriteSchema = z.object({
  entryType: z.enum(["WASTE", "SCRAP"]),
  materialCode: z.string().trim().min(1).max(80),
  quantity: preciseQuantity,
  unit: z.string().trim().min(1).max(80),
  reasonCodeId: z.string().trim().min(1).max(100),
  note: z.string().trim().max(500).nullable(),
  occurredAt: dateTime,
  idempotencyKey: z.string().trim().min(8).max(120),
  expectedRunVersion: z.number().int().positive(),
  recipeVersion: z.string().trim().min(1).max(500),
  approvalToken: z.string().trim().min(1).max(200).nullable(),
}).strict();

export const materialScrapEntrySchema = z.object({
  id: z.string().min(1),
  runId: z.string().min(1),
  entryType: z.enum(["WASTE", "SCRAP"]),
  materialCode: z.string(),
  materialName: z.string(),
  quantity: preciseQuantity,
  unit: z.string(),
  reasonCodeId: z.string(),
  reasonName: z.string(),
  note: z.string().nullable(),
  occurredAt: dateTime,
  enteredAt: dateTime,
  enteredBy: z.string(),
  enteredByName: z.string(),
  operatorSessionId: z.string(),
  terminalId: z.string(),
  runVersion: z.number().int().positive(),
  recipeVersion: z.string(),
  recipeObservedAt: dateTime,
  supervisorApprovalId: z.string().nullable(),
  idempotencyReplay: z.boolean().optional(),
}).strict();

export type MaterialScrapWrite = z.infer<typeof materialScrapWriteSchema>;
export type MaterialScrapEntry = z.infer<typeof materialScrapEntrySchema>;

export type MaterialScrapTotal = {
  materialCode: string;
  materialName: string;
  unit: string;
  entryType: MaterialScrapEntry["entryType"];
  quantity: number;
};

/** Never combines quantities whose material identity, own unit, or entry type differs. */
export function aggregateMaterialScrap(entries: readonly MaterialScrapEntry[]): MaterialScrapTotal[] {
  const groups = new Map<string, MaterialScrapTotal>();
  for (const entry of entries) {
    const key = JSON.stringify([entry.materialCode, entry.unit, entry.entryType]);
    const current = groups.get(key);
    if (current) current.quantity = Number((current.quantity + entry.quantity).toFixed(12));
    else groups.set(key, { materialCode: entry.materialCode, materialName: entry.materialName,
      unit: entry.unit, entryType: entry.entryType, quantity: entry.quantity });
  }
  return [...groups.values()];
}
